import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  EMERGENCY_CONTACT_RELATIONS,
  FORM_FIELDS_NAMES,
  GENDER,
} from "./constants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../../component-lib/Molecules/button/Button";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import moment from "moment";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import UnitNumberSelect from "../../../component-lib/Molecules/unit-number-select/UnitNumberSelect";

interface CreatePatientFormProps {
  handlePatientFieldsData: (values: any) => void;
}

const CreatePatientForm = ({
  handlePatientFieldsData,
}: CreatePatientFormProps) => {
  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
  };

  const validationSchema = Yup.object().shape({
    [FORM_FIELDS_NAMES.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [FORM_FIELDS_NAMES.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name can only contain characters"
      )
      .required("Last name is required"),

    [FORM_FIELDS_NAMES.BSA_ID]: Yup.number()
      // .matches(
      //   VALIDATION_CONSTANTS.ID,
      //   "BSA ID can only contain letters and numbers"
      // )
      .required("BSA ID is required"),
    [FORM_FIELDS_NAMES.DOB]: Yup.date()
      .max(new Date(), "Date of birth cannot be a future date")
      .required("Date Of birth is required"),
    [FORM_FIELDS_NAMES.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
    [FORM_FIELDS_NAMES.GENDER]: Yup.string().required("Gender is required"),

    [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Emergency contact name can only contain characters"
      )
      .required("Emergency contact name is required"),
    [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]: Yup.string().required(
      "Emergency contact relation is required"
    ),
    [FORM_FIELDS_NAMES.EMERGENCY_EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [FORM_FIELDS_NAMES.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CONTACT_NUMBER,
        "Invalid emergency contact number"
      )
      .required("Emergency contact number is required"),
  });

  return (
    <Formik
      initialValues={{
        [FORM_FIELDS_NAMES.FIRST_NAME]: "",
        [FORM_FIELDS_NAMES.LAST_NAME]: "",

        [FORM_FIELDS_NAMES.BSA_ID]: "",
        [FORM_FIELDS_NAMES.DOB]: undefined,
        [FORM_FIELDS_NAMES.CONTACT_NUMBER]: "",

        [FORM_FIELDS_NAMES.GENDER]: "",
        [FORM_FIELDS_NAMES.JAMBOREE_UNIT_NUMBER]: "",

        [FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME]: "",
        [FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL]: "",
        [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]: "",
        [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]: "",
        [FORM_FIELDS_NAMES.CLINIC_LOCATION]: "",
        [FORM_FIELDS_NAMES.EMERGENCY_EMAIL]: "",
        [FORM_FIELDS_NAMES.EMAIL]: "",

        [FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]: "",
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(formVals) => {
        handlePatientFieldsData(formVals);
        try {
        } catch (error) {
          console.log(error);
        }
      }}
    >
      {({
        errors,
        touched,
        submitForm,
        isSubmitting,
        handleReset,
        handleBlur,
        values,
        setFieldValue,
        handleSubmit,
      }) => {
        // Content-height form: the drawer's scroll pane handles overflow.
        // A fixed height here pinned the submit button to an off-screen bottom
        // edge on iPad.
        return (
          <Form onSubmit={handleSubmit} className="flex flex-col mt-4">
            <div className="w-full flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <Heading
                  title="Demographic Details"
                  customStyle={headerStyle}
                />

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      textFieldLabel="First Name"
                      name={FORM_FIELDS_NAMES.FIRST_NAME}
                      value={values[FORM_FIELDS_NAMES.FIRST_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.FIRST_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[FORM_FIELDS_NAMES.FIRST_NAME] &&
                        Boolean(errors[FORM_FIELDS_NAMES.FIRST_NAME])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.FIRST_NAME]
                          ? errors[FORM_FIELDS_NAMES.FIRST_NAME]
                          : undefined
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <Input
                      textFieldLabel="Last Name"
                      name={FORM_FIELDS_NAMES.LAST_NAME}
                      value={values[FORM_FIELDS_NAMES.LAST_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.LAST_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[FORM_FIELDS_NAMES.LAST_NAME] &&
                        Boolean(errors[FORM_FIELDS_NAMES.LAST_NAME])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.LAST_NAME]
                          ? errors[FORM_FIELDS_NAMES.LAST_NAME]
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      textFieldLabel="BSA ID"
                      name={FORM_FIELDS_NAMES.BSA_ID}
                      value={values[FORM_FIELDS_NAMES.BSA_ID]}
                      onChangeCb={(e) => {
                        setFieldValue(FORM_FIELDS_NAMES.BSA_ID, e.target.value);
                      }}
                      type="number"
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[FORM_FIELDS_NAMES.BSA_ID] &&
                        Boolean(errors[FORM_FIELDS_NAMES.BSA_ID])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.BSA_ID]
                          ? errors[FORM_FIELDS_NAMES.BSA_ID]
                          : undefined
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <DatePicker
                      name={FORM_FIELDS_NAMES.DOB}
                      value={values[FORM_FIELDS_NAMES.DOB]}
                      onChangeCb={(value) => {
                        setFieldValue(FORM_FIELDS_NAMES.DOB, value);
                      }}
                      label="Date of Birth"
                      style={{ width: "100%" }}
                      error={
                        touched[FORM_FIELDS_NAMES.DOB] &&
                        Boolean(errors[FORM_FIELDS_NAMES.DOB])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.DOB]
                          ? errors[FORM_FIELDS_NAMES.DOB]
                          : undefined
                      }
                      maxDate={moment()}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      textFieldLabel="Email"
                      name={FORM_FIELDS_NAMES.EMAIL}
                      value={values[FORM_FIELDS_NAMES.EMAIL]}
                      onChangeCb={(e) => {
                        setFieldValue(FORM_FIELDS_NAMES.EMAIL, e.target.value);
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[FORM_FIELDS_NAMES.EMAIL] &&
                        Boolean(errors[FORM_FIELDS_NAMES.EMAIL])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.EMAIL]
                          ? errors[FORM_FIELDS_NAMES.EMAIL]
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <ContactInput
                      textFieldLabel="Contact Number"
                      name={FORM_FIELDS_NAMES.CONTACT_NUMBER}
                      value={values[FORM_FIELDS_NAMES.CONTACT_NUMBER]}
                      onChangeCb={(value) => {
                        setFieldValue(FORM_FIELDS_NAMES.CONTACT_NUMBER, value);
                      }}
                      size="small"
                      onBlur={handleBlur}
                      error={
                        touched[FORM_FIELDS_NAMES.CONTACT_NUMBER] &&
                        Boolean(errors[FORM_FIELDS_NAMES.CONTACT_NUMBER])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.CONTACT_NUMBER]
                          ? errors[FORM_FIELDS_NAMES.CONTACT_NUMBER]
                          : undefined
                      }
                      isContactNumber={true}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <SelectDropdown
                      label="Gender"
                      name={FORM_FIELDS_NAMES.GENDER}
                      value={values[FORM_FIELDS_NAMES.GENDER]}
                      options={GENDER}
                      onChangeCb={(e) => {
                        setFieldValue(FORM_FIELDS_NAMES.GENDER, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[FORM_FIELDS_NAMES.GENDER] &&
                        Boolean(errors[FORM_FIELDS_NAMES.GENDER])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.GENDER]
                          ? errors[FORM_FIELDS_NAMES.GENDER]
                          : undefined
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <UnitNumberSelect
                      label="Unit Number"
                      allowCreate
                      onChange={(record) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.CLINIC_LOCATION,
                          record?.id || "",
                          true
                        );
                        setFieldValue(
                          FORM_FIELDS_NAMES.JAMBOREE_UNIT_NUMBER,
                          record?.unitNumber || ""
                        );
                        setFieldValue(
                          FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME,
                          record?.leaderName || ""
                        );
                        setFieldValue(
                          FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL,
                          record?.email || ""
                        );
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      disabled
                      textFieldLabel="Jamboree Unit Leader Name"
                      name={FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME}
                      value={
                        values[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME]
                      }
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME,
                          e.target.value
                        );
                      }}
                      size="small"
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME] &&
                          errors[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME]
                      )}
                      helperText={
                        touched[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME]
                          ? errors[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_NAME]
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      disabled
                      textFieldLabel="Jamboree Unit Leader Email"
                      name={FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL}
                      value={
                        values[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL]
                      }
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL,
                          e.target.value
                        );
                      }}
                      size="small"
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL] &&
                          errors[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL]
                      )}
                      helperText={
                        touched[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL]
                          ? errors[FORM_FIELDS_NAMES.JAMBOREE_UNIT_LEADER_EMAIL]
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <Heading
                  title="Emergency Contact Details"
                  customStyle={headerStyle}
                />

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      textFieldLabel="Emergency Contact Name"
                      name={FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME}
                      value={values[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME,
                          e.target.value
                        );
                      }}
                      size="small"
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME] &&
                          errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]
                      )}
                      helperText={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]
                          ? errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NAME]
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <SelectDropdown
                      label="Emergency Contact Relation"
                      name={FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION}
                      value={
                        values[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]
                      }
                      options={EMERGENCY_CONTACT_RELATIONS}
                      onChangeCb={(e) =>
                        setFieldValue(
                          FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION,
                          e.target.value
                        )
                      }
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION] &&
                          errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]
                      )}
                      helperText={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]
                          ? errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_RELATION]
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      textFieldLabel="Emergency Contact Email"
                      name={FORM_FIELDS_NAMES.EMERGENCY_EMAIL}
                      value={values[FORM_FIELDS_NAMES.EMERGENCY_EMAIL]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.EMERGENCY_EMAIL,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_EMAIL] &&
                        Boolean(errors[FORM_FIELDS_NAMES.EMERGENCY_EMAIL])
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_EMAIL]
                          ? errors[FORM_FIELDS_NAMES.EMERGENCY_EMAIL]
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <ContactInput
                      textFieldLabel="Emergency Contact Number"
                      name={FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER}
                      value={values[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]}
                      onChangeCb={(value) => {
                        setFieldValue(
                          FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER,
                          value
                        );
                      }}
                      size="small"
                      onBlur={handleBlur}
                      error={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER] &&
                        Boolean(
                          errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]
                        )
                      }
                      helperText={
                        touched[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]
                          ? errors[FORM_FIELDS_NAMES.EMERGENCY_CONTACT_NUMBER]
                          : undefined
                      }
                      isContactNumber={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky action bar — stays visible while the form scrolls, so
                the submit button can never end up below the viewport. */}
            <div className="flex justify-end sticky bottom-0 bg-white pt-4 pb-2 mt-4">
              <Button
                type="submit"
                label="Add New Patient"
                variant="contained"
                onClickCb={() => {}}
                style={{
                  padding: "10px 18px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreatePatientForm;
